import React from 'react';
import { Link } from 'react-router-dom';
import { Edit3 } from 'lucide-react';
import { ProgressBar, Button } from './ui';
import { HotelWithProgress } from '../types';
import { hotelNameToSlug } from '../utils/format';

import { cn } from '../utils/cn';

interface HotelCardProps {
  hotel: HotelWithProgress;
  onEditHotel?: (hotel: HotelWithProgress) => void;
  isAdmin?: boolean;
  className?: string;
}

export function HotelCard({ hotel, onEditHotel, isAdmin, className }: HotelCardProps) {
  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'primary';
  };

  // Removed unused function

  return (
    <Link to={`/hotel/${hotelNameToSlug(hotel.name)}`} className="block group">
      <div className={cn(
        'rounded-3xl border border-gray-200 hover:shadow-xl hover:shadow-primary-500/10 hover:border-gray-300 hover:-translate-y-2 hover:scale-105 transition-all duration-500 bg-white/30 backdrop-blur-xl overflow-hidden group relative',
        className
      )}>

        {/* Hotel Image */}
        <div className="relative h-48 bg-gray-100">
          <img
            src={hotel.imageUrl}
            alt={hotel.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
            }}
          />

          
          {/* Progress percentage indicator */}
          <div className="absolute top-4 right-4">
            <div className="inline-flex items-center justify-center bg-white/90 backdrop-blur-xl border border-white/30 text-gray-700 hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl px-3 py-2 text-sm font-semibold">
              {hotel.progressPercentage}%
            </div>
          </div>

          {/* Edit button for admins */}
          {isAdmin && onEditHotel && (
            <div className="absolute top-4 left-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditHotel(hotel);
                }}
                className="bg-white/90 backdrop-blur-xl border border-white/30 text-gray-700 hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl px-3 py-2"
              >
                <Edit3 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="p-6 relative z-10">
          <div className="space-y-4">
            {/* Hotel Header */}
            <div className="space-y-2">
              <h3 className="font-display font-bold text-xl text-gray-900 group-hover:text-primary-700 transition-colors duration-300">
                {hotel.name}
              </h3>
              <p className="text-sm text-gray-600 font-text font-medium">
                {hotel.contactPerson}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <ProgressBar
                value={hotel.progressPercentage}
                color={getProgressColor(hotel.progressPercentage)}
                size="sm"
              />
              <div className="flex justify-start">
                <span className="text-xs text-gray-400">
                  {hotel.completedTasks} of {hotel.totalTasks} tasks
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
